import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common";

export function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef();
  const nav = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
    else {
      setQ("");
      setGroups([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) {
      setGroups([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setGroups(data.groups || []);
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  if (!open) return null;

  const go = (link) => {
    onClose();
    if (link) nav(link);
  };

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative max-w-2xl mx-auto px-4 iks-pop" style={{ marginTop: "calc(4rem + env(safe-area-inset-top))" }}>
        <div className="iks-card overflow-hidden">
          <div className="flex items-center gap-3 px-4 border-b border-white/10">
            <Search className="text-sky-400 shrink-0" size={20} />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search everything you can see…"
              data-testid="search-input"
              className="flex-1 bg-transparent py-4 text-slate-100 placeholder-slate-500 outline-none text-base"
            />
            <button onClick={onClose} className="text-slate-400 hover:text-slate-100 p-1">
              <X size={20} />
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {loading && <p className="p-6 text-center text-slate-500 text-sm">Searching…</p>}
            {!loading && q.trim().length >= 2 && groups.length === 0 && (
              <p className="p-8 text-center text-slate-500 text-sm" data-testid="search-empty">
                No results found for "{q}". Only things you're allowed to see appear here.
              </p>
            )}
            {!loading && q.trim().length < 2 && (
              <p className="p-8 text-center text-slate-500 text-sm">
                Try "Subway", a food, a person, a bill…
              </p>
            )}
            {groups.map((g, gi) => (
              <div key={gi} className="border-b border-white/5 last:border-0" data-testid="search-group">
                <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                  {g.photo !== undefined && (
                    <Avatar photo={g.photo} name={g.person || g.source} size={20} ring={false} />
                  )}
                  <p className="text-xs font-semibold uppercase tracking-wider text-sky-400">
                    {g.source}
                  </p>
                </div>
                {g.results.map((r, ri) => (
                  <button
                    key={ri}
                    onClick={() => go(g.link)}
                    data-testid="search-result"
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-white/5 text-left transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-slate-100 truncate">{r.title}</p>
                      {r.subtitle && <p className="text-xs text-slate-500 truncate">{r.subtitle}</p>}
                    </div>
                    <ArrowUpRight className="text-slate-600 shrink-0" size={16} />
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
